import { useCallback, useRef } from "react"

/**
 * Generic execution queue item
 */
export interface ExecutionQueueItem<TKey = string> {
  /** Unique identifier for the execution item */
  id: TKey
  /** The function to execute */
  executeFunction: () => Promise<void> | Promise<boolean> | void
  /** Reason for execution (for debugging/logging) */
  reason: string
}

/**
 * Configuration options for sequential execution
 */
export interface SequentialExecutionConfig {
  /** Delay between executions in milliseconds (default: 100) */
  delayBetweenExecutions?: number
  /** Whether to log errors to console (default: true) */
  logErrors?: boolean
  /** Custom error handler */
  onError?: (id: string, error: unknown, reason: string) => void
}

/**
 * Hook to manage sequential execution of async operations
 * Prevents concurrent execution that could crash servers or cause race conditions
 *
 * @param config Configuration options for the sequential execution
 * @returns Object with methods to manage sequential execution
 */
export const useSequentialExecution = <TKey = string>(
  config: SequentialExecutionConfig = {}
) => {
  const { delayBetweenExecutions = 100, logErrors = true, onError } = config

  // Execution state
  const executionQueue = useRef<ExecutionQueueItem<TKey>[]>([])
  const isExecuting = useRef<boolean>(false)

  /**
   * Process the execution queue sequentially
   */
  const processExecutionQueue = useCallback(async (): Promise<void> => {
    if (isExecuting.current || executionQueue.current.length === 0) {
      return
    }

    isExecuting.current = true

    while (executionQueue.current.length > 0) {
      const { id, executeFunction, reason } = executionQueue.current.shift()!

      try {
        await executeFunction()

        // Add delay between executions to prevent server overload
        if (delayBetweenExecutions > 0) {
          await new Promise((resolve) =>
            setTimeout(resolve, delayBetweenExecutions)
          )
        }
      } catch (error) {
        if (logErrors) {
          console.error(`Error executing ${String(id)}:`, error)
        }

        if (onError) {
          onError(String(id), error, reason)
        }
        // Continue with next item even if one fails
      }
    }

    isExecuting.current = false
  }, [delayBetweenExecutions, logErrors, onError])

  /**
   * Add an execution request to the queue
   * Prevents duplicates based on the id
   */
  const requestExecution = useCallback(
    (
      id: TKey,
      executeFunction: () => Promise<void> | Promise<boolean> | void,
      reason: string = "user_initiated"
    ): void => {
      // Check if already queued to prevent duplicates
      const alreadyQueued = executionQueue.current.some(
        (item) => item.id === id
      )

      if (!alreadyQueued) {
        executionQueue.current.push({
          id,
          executeFunction,
          reason,
        })
        processExecutionQueue()
      }
    },
    [processExecutionQueue]
  )

  /**
   * Clear the execution queue
   */
  const clearQueue = useCallback((): void => {
    executionQueue.current = []
  }, [])

  /**
   * Get current queue status
   */
  const getQueueStatus = useCallback(
    () => ({
      isExecuting: isExecuting.current,
      queueLength: executionQueue.current.length,
      queuedItems: executionQueue.current.map((item) => ({
        id: item.id,
        reason: item.reason,
      })),
    }),
    []
  )

  /**
   * Remove a specific item from the queue
   */
  const removeFromQueue = useCallback((id: TKey): boolean => {
    const initialLength = executionQueue.current.length
    executionQueue.current = executionQueue.current.filter(
      (item) => item.id !== id
    )
    return executionQueue.current.length < initialLength
  }, [])

  return {
    requestExecution,
    clearQueue,
    getQueueStatus,
    removeFromQueue,
    isExecuting: () => isExecuting.current,
  }
}

export default useSequentialExecution
